import { useEffect, useState } from "react";
import { shellVersion } from "./api";

/**
 * What the site does for customers who order in the phone's browser instead of the Android app:
 * a sound, a vibration and a notification when their order changes (while the page is open, even in
 * the background), and an offer to put the site on the home screen so it opens like an app.
 * The Android app does its own notifications, so none of this runs there.
 */
export const inBrowser = shellVersion() === null;
const standalone = () => matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;

// ---------------------------------------------------------------- the service worker (needed for notifications on Android)
let worker: Promise<ServiceWorkerRegistration | null> = Promise.resolve(null);
if (inBrowser && "serviceWorker" in navigator) worker = navigator.serviceWorker.register("/sw.js").catch(() => null);

// ---------------------------------------------------------------- a sound the page may play once the customer has tapped something
let audio: AudioContext | null = null;
const unlock = () => {
  try { audio ??= new AudioContext(); void audio.resume(); } catch { /* no sound on this browser */ }
  removeEventListener("pointerdown", unlock);
};
if (inBrowser) addEventListener("pointerdown", unlock);

function chime() {
  if (!audio) return;
  const start = audio.currentTime;
  for (const [offset, pitch] of [[0, 880], [0.18, 1175], [0.36, 1568]] as const) {
    const tone = audio.createOscillator(), volume = audio.createGain();
    tone.frequency.value = pitch;
    volume.gain.setValueAtTime(0.0001, start + offset);
    volume.gain.exponentialRampToValueAtTime(0.4, start + offset + 0.02);
    volume.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.35);
    tone.connect(volume).connect(audio.destination);
    tone.start(start + offset);
    tone.stop(start + offset + 0.4);
  }
}

// ---------------------------------------------------------------- notifications
export type AlertPermission = NotificationPermission | "unsupported";
export const alertPermission = (): AlertPermission => "Notification" in window ? Notification.permission : "unsupported";
export const askForAlerts = async (): Promise<AlertPermission> => {
  unlock();
  if (!("Notification" in window)) return "unsupported";
  try { return await Notification.requestPermission(); } catch { return Notification.permission; }
};

/** Tells the customer their order changed: always a sound and a buzz, and a notification when the page is not in front. */
export async function alertCustomer(title: string, body: string) {
  if (!inBrowser) return;
  chime();
  try { navigator.vibrate?.([300, 120, 300, 120, 300]); } catch { /* no vibration */ }
  if (document.visibilityState === "hidden") flashTitle(title);
  if (alertPermission() !== "granted" || document.visibilityState === "visible") return;
  const options: NotificationOptions = { body, icon: "/icon-192.png", badge: "/icon-192.png", tag: location.hash, data: { url: location.href } };
  const registration = await worker;
  try {
    if (registration) await registration.showNotification(title, options);
    else new Notification(title, options); // desktop browsers without the worker
  } catch { /* the sound has already played */ }
}

/** The tab title shows the news until the customer looks at the page again. */
let plainTitle = "";
function flashTitle(text: string) {
  plainTitle ||= document.title;
  document.title = `● ${text}`;
  const restore = () => { if (document.visibilityState !== "visible") return; document.title = plainTitle; plainTitle = ""; document.removeEventListener("visibilitychange", restore); };
  document.addEventListener("visibilitychange", restore);
}

// ---------------------------------------------------------------- "add to home screen"
type InstallPrompt = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };
let deferred: InstallPrompt | null = null;
const installListeners = new Set<() => void>();
addEventListener("beforeinstallprompt", (event) => { event.preventDefault(); deferred = event as InstallPrompt; installListeners.forEach((listener) => listener()); });
addEventListener("appinstalled", () => { deferred = null; installListeners.forEach((listener) => listener()); });

const DISMISSED = "mm.order.installDismissed";
const dismissedRecently = () => { try { return Date.now() - Number(localStorage.getItem(DISMISSED) ?? 0) < 14 * 86_400_000; } catch { return false; } };
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

/**
 * `prompt` when the browser can add the site itself (Chrome, Edge, Samsung Internet), `ios` when the customer
 * has to do it from Safari's Share menu, and null when it is already installed, inside the Android app, or dismissed.
 */
export function useInstallOffer() {
  const [, rerender] = useState(0);
  const [dismissed, setDismissed] = useState(dismissedRecently);
  useEffect(() => { const listener = () => rerender((n) => n + 1); installListeners.add(listener); return () => { installListeners.delete(listener); }; }, []);
  const kind: "prompt" | "ios" | null = !inBrowser || standalone() || dismissed ? null : deferred ? "prompt" : isIos() ? "ios" : null;
  return {
    kind,
    install: async () => { if (!deferred) return; await deferred.prompt(); await deferred.userChoice.catch(() => undefined); deferred = null; rerender((n) => n + 1); },
    dismiss: () => { try { localStorage.setItem(DISMISSED, String(Date.now())); } catch { /* shown again next visit */ } setDismissed(true); }
  };
}
